/**
 * The *public* hostname of a request — the one the visitor typed, which is the tenant key here
 * (`cinema_configuration.domain`).
 *
 * Today that is simply the `Host` header. If a proxy is ever put in front, the public host moves to
 * a forwarded header and `Host` becomes the shared origin, identical for every customer; that
 * header must then be authenticated before it is believed, because an unauthenticated one is a
 * tenant switch anyone can send (`curl origin -H 'X-Original-Host: www.client-b.com'`).
 */

/** The subset of `Headers` / `ReadonlyHeaders` this module needs — both satisfy it structurally. */
export type HeaderReader = {
    get(name: string): string | null | undefined;
};

/** Hosts that are not the cinema's configured domain but must resolve to it anyway. */
const DOMAIN_ALIASES: Record<string, string> = {
    'preview.ufa-duesseldorf.cineamo.dev': 'ufa-duesseldorf.de'
};

/** Proxies may append rather than replace, so a header value can be a chain — the first entry wins. */
export function normalizeHost(raw: string | null | undefined): string {
    return (raw ?? '').split(',')[0].trim().toLowerCase().replace(/:\d+$/, '');
}

/**
 * `x-forwarded-host` sits below `host` rather than above it: on Vercel `host` already carries the
 * request's own hostname, while `x-forwarded-host` is spoofable wherever no intermediary overwrites
 * it. It stays only for the case where `host` is absent.
 */
export function resolvePublicHost(headers: HeaderReader): string {
    return normalizeHost(headers.get('host')) || normalizeHost(headers.get('x-forwarded-host'));
}

/**
 * Absolute public origin, for the URLs that end up in HTML: canonical, OpenGraph, hreflang, JSON-LD,
 * sitemaps, robots. Defaults to https — only an origin hop can be plain http, and its scheme must
 * never reach the page.
 */
export function resolvePublicOrigin(headers: HeaderReader): string {
    const host = resolvePublicHost(headers);
    if (!host) {
        return '';
    }
    const scheme = normalizeHost(headers.get('x-forwarded-proto')) || 'https';
    return `${scheme}://${host}`;
}

/**
 * The public host reduced to the key the API stores in `cinema_configuration.domain`: no `www.`,
 * aliases applied. Staging and local hold their own values in that column, so no environment needs
 * special-casing here.
 */
export function toCinemaDomain(publicHost: string): string {
    const host = normalizeHost(publicHost);
    const domain = host.startsWith('www.') ? host.slice(4) : host;
    return DOMAIN_ALIASES[domain] ?? domain;
}

/** A sales demo site built for a prospect (`<slug>.demo.cineamo.dev`), see DemoWebsiteDomain in core. */
export function isSalesDemoHost(publicHost: string | undefined): boolean {
    return normalizeHost(publicHost).endsWith('.demo.cineamo.dev');
}

/**
 * Demo sites (`<slug>.cineamo.dev`, `<slug>.demo.cineamo.dev`) and staging all live under
 * `.cineamo.dev`; none of them may ever be indexed. Customer domains are never under it.
 */
export function isNonIndexableHost(publicHost: string): boolean {
    return normalizeHost(publicHost).endsWith('.cineamo.dev');
}
